import React, { useState, useId, useRef, useEffect } from "react";
import MenuItem from "./MenuItem"; // Make sure this import is present

// Utility: trigger a temporary ripple span inside the nearest .formControl
const Ripple = (e, { duration = 1000, className = "ripple animate" } = {}) => {
  const wrapper = e?.target?.closest?.(".formControl");
  if (!wrapper) return;
  const span = document.createElement("span");
  span.className = className;
  wrapper.appendChild(span);
  setTimeout(() => span.remove(), duration);
};
const RadioButtons = ({
  label,
  name = "radio-group",
  checked,
  onChange = () => {},
}) => {
  const autoId = useId();
  const rid = `${name}-${autoId}`;

  return (
    <div className="formControl radio">
      <input
        type="radio"
        id={rid}
        name={name}
        value={label}
        checked={checked}
        onChange={(e) => {
          Ripple(e);
          onChange(e);
        }}
      />
      <label className="inputLabel t-label t-field-label" htmlFor={rid}>
        {label}
      </label>
    </div>
  );
};

const Checkbox = ({
  label = "Checkbox",
  checked,
  defaultChecked,
  onChange = (e) => {},
  onClick = () => {},
  id,
  disabled = false,
}) => {
  const autoId = useId();
  const cid = id || autoId;

  return (
    <div className="formControl checkbox">
      <input
        className="checkbox-trigger visuallyhidden"
        type="checkbox"
        id={cid}
        disabled={disabled}
        checked={checked}
        defaultChecked={defaultChecked}
        onChange={(e) => {
          Ripple(e);
          onChange(e);
        }}
        onClick={onClick}
      />
      <label className="inputLabel t-label t-field-label" htmlFor={cid}>
        {label}
      </label>
    </div>
  );
};

const ToggleSwitch = ({
  label,
  defaultChecked = false,
  onClick = () => {},
  id,
  icon = null,
  iconOn = null,
  iconOff = null,
}) => {
  const [isChecked, setIsChecked] = useState(defaultChecked);
  const autoId = useId();
  const tid = id || `toggle-${autoId}`;

  const handleToggle = () => {
    const newState = !isChecked;
    setIsChecked(newState);
    onClick(newState);
  };

  const activeIcon = isChecked ? (iconOn ?? icon) : (iconOff ?? icon);

  const renderIcon = (iconValue) => {
    if (!iconValue) return null;
    return typeof iconValue === "string" ? (
      <span className="material-icons sliderHandleIcon">{iconValue}</span>
    ) : (
      iconValue
    );
  };

  return (
    <div className="formControl toggleSwitch">
      <input
        type="checkbox"
        id={tid}
        className="toggleInput"
        checked={isChecked}
        onChange={handleToggle}
      />
      <span
        className={`slider${isChecked ? " active" : ""}`}
        onClick={handleToggle}
        role="switch"
        aria-checked={isChecked}
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            handleToggle();
          }
        }}
      >
        <span className={"sliderHandle"}>{renderIcon(activeIcon)}</span>
      </span>
      <label
        className="inputLabel t-label t-field-label"
        htmlFor={tid}
        onClick={handleToggle}
      >
        {label}
      </label>
    </div>
  );
};

export default ToggleSwitch;

const TextInput = ({
  leftIcon,
  rightIcon,
  label,
  value,
  placeholder,
  type = "text",
  onChange = () => {},
  id,
  labelPosition = "top",
  // textarea support
  as,
  multiline = false,
  rows = 3,
}) => {
  const autoId = useId();
  const tid = id || `${label || "text-input"}-${autoId}`;
  const isTextArea = as === "textarea" || multiline === true;
  const labelClass =
    labelPosition === "left"
      ? isTextArea
        ? "input-wrapper label-top"
        : "input-wrapper label-left"
      : "input-wrapper";

  return (
    <div className="formControl input-field">
      <div className={labelClass}>
        <label htmlFor={tid} className="inputLabel t-label t-field-label">
          {label}
        </label>
        <div className="inputArea">
          {isTextArea ? (
            <textarea
              id={tid}
              value={value}
              placeholder={placeholder}
              onChange={onChange}
              rows={rows}
            />
          ) : (
            <input
              type={type}
              id={tid}
              value={value}
              placeholder={placeholder}
              onChange={onChange}
            />
          )}
          <span className="rightIcon material-icons">
            {rightIcon || leftIcon}
          </span>
        </div>
      </div>
    </div>
  );
};

const DropDown = ({
  icon = "expand_more",
  leftIcon,
  label,
  value,
  placeholder,
  onChange = () => {},
  id,
  options = [],
  onSelect = () => {},
  disabled = false,
  labelPosition = "top",
  displayMode = "text",
  iconOnly = false,
  className = "",
}) => {
  const autoId = useId();
  const tid = id || `${label || "text-input"}-${autoId}`;
  const [isOpen, setIsOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const [openUp, setOpenUp] = useState(false);
  const [menuAlign, setMenuAlign] = useState("left");
  const closeTimerRef = useRef(null);
  const dropdownRef = useRef(null);
  const labelClass =
    labelPosition === "left" ? "input-wrapper label-left" : "input-wrapper";

  const startClose = () => {
    if (isClosing) return;
    setIsClosing(true);
    // Faster exit to keep UI responsive
    // Base (~160ms) + light stagger per item (20ms each) + small buffer
    const perItem = 20;
    const base = 160;
    const buffer = 60;
    const total =
      base + Math.max(0, (options?.length || 0) - 1) * perItem + buffer;
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => {
      setIsClosing(false);
      setIsOpen(false);
    }, total);
  };

  const handleToggle = () => {
    if (disabled) return;
    if (isOpen) {
      startClose();
    } else {
      setIsOpen(true);
    }
  };

  const handleSelect = (opt) => {
    const selectedValue =
      typeof opt === "object" && opt !== null ? opt.value ?? opt.label : opt;
    onSelect(selectedValue);
    onChange({ target: { id: tid, value: selectedValue } });
    startClose();
  };

  const renderLabel = (opt) =>
    typeof opt === "object" && opt !== null
      ? opt.label ?? String(opt.value ?? "")
      : String(opt);

  const normalizedValue = value ?? "";
  const selectedOption = options.find((opt) => {
    const optLabel = renderLabel(opt);
    const optValue =
      typeof opt === "object" && opt !== null ? opt.value ?? optLabel : optLabel;
    return String(optValue) === String(normalizedValue);
  });
  const selectedIcon =
    selectedOption && typeof selectedOption === "object"
      ? selectedOption.iconLeft ?? selectedOption.icon
      : null;
  const resolvedLeftIcon =
    leftIcon || (displayMode === "icon" ? selectedIcon : null);
  const showSelectedLabel = displayMode !== "icon" && !iconOnly;

  useEffect(() => {
    if (!isOpen) return;
    const frame = requestAnimationFrame(() => {
      const root = dropdownRef.current;
      if (!root) return;
      const rect = root.getBoundingClientRect();
      const menu = root.querySelector(".dropdown-menu");
      const menuHeight = menu?.offsetHeight || 320;
      const menuWidth = menu?.offsetWidth || 256;
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 0;
      const viewportWidth =
        window.innerWidth || document.documentElement.clientWidth || 0;
      const spaceBelow = Math.max(0, viewportHeight - rect.bottom);
      const spaceAbove = Math.max(0, rect.top);
      const shouldOpenUp = spaceBelow < menuHeight && spaceAbove > spaceBelow;
      const spaceRight = Math.max(0, viewportWidth - rect.right);
      const spaceLeft = Math.max(0, rect.left);
      const shouldAlignRight =
        spaceRight < menuWidth && spaceLeft > spaceRight;
      setOpenUp(shouldOpenUp);
      setMenuAlign(shouldAlignRight ? "right" : "left");
    });
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        startClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      cancelAnimationFrame(frame);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  useEffect(
    () => () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    },
    []
  );

  return (
    <div
      ref={dropdownRef}
      className={`formControl dropDown${isOpen ? " open" : ""}${
        isClosing ? " closing" : ""
      }${openUp ? " open-up" : ""} menu-${menuAlign}${
        disabled ? " disabled" : ""
      }${className ? ` ${className}` : ""}`}
      data-open={isOpen}
      data-count={options?.length || 0}
      style={{ "--total-items": options?.length || 0 }}
    >
      <div className={labelClass}>
        <label htmlFor={tid} className="inputLabel t-label t-field-label">
          {label}
        </label>

        <div className="inputAreaWrap">
          <div
            className={`inputArea${resolvedLeftIcon ? " has-left-icon" : ""}${
              iconOnly ? " icon-only" : ""
            }`}
            onClick={handleToggle}
            role="button"
            aria-expanded={isOpen}
            aria-controls={`${tid}-menu`}
            tabIndex={disabled ? -1 : 0}
            onKeyDown={(e) => {
              if (disabled) return;
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                handleToggle();
              } else if (e.key === "Escape") {
                startClose();
              }
            }}
          >
            {resolvedLeftIcon && (
              <span className="leftIcon material-icons">
                {resolvedLeftIcon}
              </span>
            )}
            <input
              type="text"
              id={tid}
              value={showSelectedLabel ? normalizedValue : ""}
              placeholder={placeholder}
              onChange={onChange}
              readOnly
              aria-haspopup="listbox"
            />
            <span className="rightIcon material-icons">{icon}</span>
          </div>

          {(isOpen || isClosing) && options.length > 0 && (
            <div
              id={`${tid}-menu`}
              className="dropdown-menu"
              role="listbox"
              aria-labelledby={tid}
            >
              {options.map((opt, i) => {
                const optLabel = renderLabel(opt);
                const optValue =
                  typeof opt === "object" && opt !== null
                    ? opt.value ?? optLabel
                    : optLabel;
                const isSelected = String(normalizedValue) === String(optValue);
                const optIconLeft =
                  typeof opt === "object" && opt !== null
                    ? opt.iconLeft ?? opt.icon
                    : null;
                const optIconRight =
                  typeof opt === "object" && opt !== null ? opt.iconRight : null;
                const iconSide =
                  optIconLeft && optIconRight
                    ? "both"
                    : optIconRight
                    ? "right"
                    : "left";
                const delay = isClosing
                  ? Math.max(0, (options.length || 0) - 1 - i) * 0.03
                  : i * 0.04;
                const enterAnim = openUp
                  ? "animate__bounceInUp"
                  : "animate__bounceInDown";
                const exitAnim = openUp
                  ? "animate__bounceOutDown"
                  : "animate__bounceOutUp";
                return (
                  <MenuItem
                    key={i}
                    css={`dropdown-item animate__animated ${
                      isClosing ? exitAnim : enterAnim
                    }${isSelected ? " selected" : ""}`}
                    icon={optIconLeft || optIconRight || opt.icon}
                    iconLeft={optIconLeft}
                    iconRight={optIconRight}
                    side={iconSide}
                    showLabel={true}
                    role="option"
                    aria-selected={isSelected}
                    tabIndex={0}
                    style={{ animationDelay: `${delay}s`, animationDuration: "0.25s" }}
                    onClick={() => handleSelect(opt)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleSelect(opt);
                      }
                    }}
                  >
                    {optLabel}
                  </MenuItem>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export { RadioButtons, Checkbox, ToggleSwitch, TextInput, DropDown };
